import React from 'react';
import theme from '@css/theme';
import { Button, ButtonProps as AntButtonProps, TooltipProps } from 'antd';
import styled, { css } from 'styled-components';
import light from '@ui-library/tokens/light.json';
import { nextVariant } from '@ui-library/styles';

type ExtendedSizeType = AntButtonProps['size'] | 'xlarge';
type ExtendedType = AntButtonProps['type'] | 'secondary';

type NextLevel = 'primary' | 'secondary' | 'tertiary';
type NextIntent = 'default' | 'destructive';

export interface ButtonProps extends Omit<AntButtonProps, 'size' | 'type'> {
  type?: ExtendedType;
  size?: ExtendedSizeType;
  variant?: Variant | 'next';
  level?: NextLevel;
  intent?: NextIntent;
  tooltip?: TooltipProps & { show?: boolean };
  noPadding?: boolean;
}

const antSizeToTokenSize = {
  large: 'fontSize',
  middle: 'fontSize',
  small: 'fontSizeSM',
  xlarge: 'fontSizeLG',
} satisfies Record<Exclude<ExtendedSizeType, undefined>, keyof typeof theme.token>;

const defaultTypeColors = {
  default: {
    border: theme.token.colorBorder,
    color: theme.token.colorText,
    hoverBackground: theme.token.colorBgTextHover,
    activeBackground: theme.token.colorBgTextActive,
    outlineColor: theme.token.colorBorder,
  },
  danger: {
    border: theme.token.colorErrorBorder,
    color: theme.token.colorError,
    hoverBackground: theme.token.colorErrorBg,
    activeBackground: theme.token.colorErrorBgHover,
    outlineColor: theme.token.colorErrorBorder,
  },
  confirm: {
    border: theme.token.colorSuccessBorder,
    color: theme.token.colorSuccess,
    hoverBackground: theme.token.colorSuccessBg,
    activeBackground: theme.token.colorSuccessBgHover,
    outlineColor: theme.token.colorSuccessBorder,
  },
};

export type Variant = keyof typeof defaultTypeColors;
export type CustomSize = keyof typeof antSizeToTokenSize;

/** Narrows the full variant union to the old color-intent type (safe — 'next' is handled upstream). */
const asOldVariant = (v: ButtonProps['variant']): Variant =>
  v === 'next' || v === undefined ? 'default' : v;

const mapSizeToWeight = {
  large: 600,
  middle: 500,
  small: 400,
  xlarge: 600,
} as const;

const commonButtonStyles = css<ButtonProps>`
  font-weight: ${({ size = 'middle' }) => mapSizeToWeight[size]};

  &:disabled {
    /* prevent layout shift with an invisible border */
    border-color: rgba(0, 0, 0, 0);
    /* Achieves the same effect without opacity, and works correctly on dark backgrounds */
    background-color: ${({ theme }) => theme.token['gray.4']};
  }

  &:not(:disabled):focus {
    outline-offset: 2px !important;
    outline-width: 2px !important;
  }
`;

export const DefaultButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button type="default" size={size === 'xlarge' ? 'large' : size} {...rest} />;
})<ButtonProps>`
  ${commonButtonStyles}

  border-color: ${({ variant }) => defaultTypeColors[asOldVariant(variant)].border};
  color: ${({ variant }) => defaultTypeColors[asOldVariant(variant)].color};

  &:hover:enabled {
    border-color: ${({ variant }) => defaultTypeColors[asOldVariant(variant)].border} !important;
    color: ${({ variant }) => defaultTypeColors[asOldVariant(variant)].color} !important;
    background: ${({ variant }) =>
      defaultTypeColors[asOldVariant(variant)].hoverBackground} !important;
  }

  &:active:enabled {
    border-color: ${({ variant }) => defaultTypeColors[asOldVariant(variant)].border} !important;
    color: ${({ variant }) => defaultTypeColors[asOldVariant(variant)].color} !important;
    background: ${({ variant }) =>
      defaultTypeColors[asOldVariant(variant)].activeBackground} !important;
  }

  &focus:enabled {
    outline-color: ${({ variant }) =>
      defaultTypeColors[asOldVariant(variant)].outlineColor} !important;
  }
`;

const primaryButtonColors = {
  default: {
    background: theme.token.colorPrimaryBase,
    hoverBackground: theme.token.colorPrimaryHover,
    activeBackground: theme.token.colorPrimaryActive,
    outlineColor: theme.token.colorPrimaryBorder,
  },
  danger: {
    background: theme.token.colorErrorBase,
    hoverBackground: theme.token.colorErrorHover,
    activeBackground: theme.token.colorErrorActive,
    outlineColor: theme.token.colorErrorBorder,
  },
  confirm: {
    background: theme.token.colorSuccessVariant,
    hoverBackground: theme.token.colorSuccessHover,
    activeBackground: theme.token.colorSuccessActive,
    outlineColor: theme.token.colorSuccessBorder,
  },
};

export const PrimaryButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button type="primary" size={size === 'xlarge' ? 'large' : size} {...rest} />;
})<ButtonProps>`
  ${commonButtonStyles}

  background: ${({ variant }) => primaryButtonColors[asOldVariant(variant)].background};

  &:hover:enabled {
    background: ${({ variant }) =>
      primaryButtonColors[asOldVariant(variant)].hoverBackground} !important;
  }

  &:active:enabled {
    background: ${({ variant }) =>
      primaryButtonColors[asOldVariant(variant)].activeBackground} !important;
  }

  &:focus:enabled {
    outline-color: ${({ variant }) =>
      primaryButtonColors[asOldVariant(variant)].outlineColor} !important;
  }
`;

const secondaryTypeColors = {
  default: {
    color: theme.token.colorPrimaryText,
    background: theme.token.colorPrimarySubdued,
    hoverBackground: theme.token.colorPrimarySubduedHover,
    activeBackground: theme.token.colorPrimarySubduedActive,
    outlineColor: theme.token.colorPrimaryBorder,
  },
  danger: {
    color: theme.token.colorErrorText,
    background: theme.token.colorErrorSubdued,
    hoverBackground: theme.token.colorErrorSubduedHover,
    activeBackground: theme.token.colorErrorSubduedActive,
    outlineColor: theme.token.colorErrorBorder,
  },
  confirm: {
    color: theme.token.colorSuccessText,
    background: theme.token.colorSuccessSubdued,
    hoverBackground: theme.token.colorSuccessSubduedHover,
    activeBackground: theme.token.colorSuccessSubduedActive,
    outlineColor: theme.token.colorSuccessBorder,
  },
};

export const SecondaryButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button size={size === 'xlarge' ? 'large' : size} {...rest} />;
})<ButtonProps>`
  ${commonButtonStyles}

  color: ${({ variant }) => secondaryTypeColors[asOldVariant(variant)].color};

  /* prevent layout shift with an invisible border */
  border-color: rgba(0, 0, 0, 0) !important;

  background: ${({ variant }) => secondaryTypeColors[asOldVariant(variant)].background};

  &:hover:enabled {
    background: ${({ variant }) =>
      secondaryTypeColors[asOldVariant(variant)].hoverBackground} !important;
    color: ${({ variant }) => secondaryTypeColors[asOldVariant(variant)].color} !important;
  }

  &:active:enabled {
    color: ${({ variant }) => secondaryTypeColors[asOldVariant(variant)].color} !important;
    background: ${({ variant }) =>
      secondaryTypeColors[asOldVariant(variant)].activeBackground} !important;
  }

  &:focus:enabled {
    outline-color: ${({ variant }) =>
      secondaryTypeColors[asOldVariant(variant)].outlineColor} !important;
  }
`;

export const LinkButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button type={type !== 'link' && type !== 'text' ? 'link' : type} {...rest} />;
})<ButtonProps>`
  ${({ noPadding }) => noPadding && 'padding-inline: 0 !important;'}

  font-weight: ${({ size = 'middle' }) => mapSizeToWeight[size]};

  font-size: ${({ size = 'middle' }) => `${theme.token[antSizeToTokenSize[size]]}px !important;`}

  line-height: ${({ size = 'middle' }) => `${theme.token[antSizeToTokenSize[size]]}px;`}

  > *:hover:enabled,
  > *:focus:enabled {
    cursor: pointer;
    text-decoration: underline;
    outline: none;
  }

  &focus-visible:enabled {
    outline: 0;
    outline-offset: 0;
  }
`;

/* ─── Next variant ─────────────────────────────────────────────────── */

const { next } = light;

const nextButtonColors = {
  'primary-default': {
    bg: next.colorBorderFocus,
    text: next.palette.grey0,
    border: 'transparent',
    hoverBg: next.colorBorderFocus,
    hoverBorder: 'transparent',
    hoverShadow: '0 0 0 2px rgba(16, 94, 213, 0.2)',
    pressedBg: next.colorBorderFocus,
    pressedOverlay: 'rgba(0, 0, 0, 0.12)',
    pressedShadow: '0 0 0 2px rgba(16, 94, 213, 0.2)',
    disabledBg: next.palette.grey300,
    disabledText: next.palette.grey25,
    disabledBorder: 'transparent',
  },
  'primary-destructive': {
    bg: next.colorError,
    text: next.palette.grey0,
    border: 'transparent',
    hoverBg: next.colorError,
    hoverBorder: 'transparent',
    hoverShadow: '0 0 0 2px rgba(178, 0, 37, 0.2)',
    pressedBg: next.colorError,
    pressedOverlay: 'rgba(0, 0, 0, 0.12)',
    pressedShadow: '0 0 0 2px rgba(178, 0, 37, 0.2)',
    disabledBg: next.palette.grey300,
    disabledText: next.palette.grey25,
    disabledBorder: 'transparent',
  },
  'secondary-default': {
    bg: next.palette.grey0,
    text: next.colorText,
    border: next.palette.grey200,
    hoverBg: next.palette.grey0,
    hoverBorder: next.palette.grey300,
    hoverShadow: '0 0 0 2px rgba(182, 192, 205, 0.2)',
    pressedBg: next.palette.grey25,
    pressedOverlay: undefined,
    pressedShadow: '0 0 0 2px rgba(182, 192, 205, 0.2)',
    disabledBg: next.palette.grey0,
    disabledText: next.palette.grey300,
    disabledBorder: next.palette.grey200,
  },
  'secondary-destructive': {
    bg: next.palette.grey0,
    text: next.colorError,
    border: next.palette.grey200,
    hoverBg: next.palette.grey0,
    hoverBorder: next.colorError,
    hoverShadow: '0 0 0 2px rgba(178, 0, 37, 0.12)',
    pressedBg: next.palette.red50,
    pressedOverlay: undefined,
    pressedShadow: '0 0 0 2px rgba(178, 0, 37, 0.12)',
    disabledBg: next.palette.grey0,
    disabledText: next.palette.grey300,
    disabledBorder: next.palette.grey200,
  },
  'tertiary-default': {
    bg: next.palette.grey0,
    text: next.colorText,
    border: 'transparent',
    hoverBg: next.palette.grey50,
    hoverBorder: 'transparent',
    hoverShadow: 'none',
    pressedBg: next.palette.grey75,
    pressedOverlay: undefined,
    pressedShadow: 'none',
    disabledBg: next.palette.grey0,
    disabledText: next.palette.grey300,
    disabledBorder: 'transparent',
  },
  'tertiary-destructive': {
    bg: next.palette.grey0,
    text: next.colorError,
    border: 'transparent',
    hoverBg: next.palette.red50,
    hoverBorder: 'transparent',
    hoverShadow: 'none',
    pressedBg: next.palette.red50,
    pressedOverlay: 'rgba(164, 36, 62, 0.06)',
    pressedShadow: 'none',
    disabledBg: next.palette.grey0,
    disabledText: next.palette.grey300,
    disabledBorder: 'transparent',
  },
} as const;

type NextColorKey = `${NextLevel}-${NextIntent}`;

interface NextButtonInnerProps extends AntButtonProps {
  $level: NextLevel;
  $intent: NextIntent;
}

const nextButtonMinimalStyles = css<NextButtonInnerProps>`
  ${({ $level, $intent }) => {
    const key: NextColorKey = `${$level}-${$intent}`;
    const c = nextButtonColors[key];

    return css`
      &.ant-btn {
        background: ${c.bg} !important;
        color: ${c.text} !important;
        border: 1px solid ${c.border} !important;
        box-shadow: none;
        font-weight: ${next.fontWeight500};
        gap: 4px;

        /* hover */
        &:hover:not(:disabled):not(:active) {
          background: ${c.hoverBg} !important;
          border-color: ${c.hoverBorder} !important;
          box-shadow: ${c.hoverShadow} !important;
          color: ${c.text} !important;
        }

        /* pressed / active */
        &:active:not(:disabled) {
          background: ${c.pressedBg} !important;
          ${c.pressedOverlay
            ? `background: linear-gradient(${c.pressedOverlay}, ${c.pressedOverlay}), ${c.pressedBg} !important;`
            : ''}
          border-color: ${c.hoverBorder} !important;
          box-shadow: ${c.pressedShadow} !important;
          color: ${c.text} !important;
        }

        /* keyboard focus */
        &:focus-visible:not(:disabled) {
          ${nextVariant.focusRing}
          outline: none !important;
        }

        /* disabled */
        &:disabled {
          background: ${c.disabledBg} !important;
          color: ${c.disabledText} !important;
          border-color: ${c.disabledBorder} !important;
          box-shadow: none !important;
          cursor: not-allowed;
        }
      }
    `;
  }}
`;

export const NextButton = styled((props: NextButtonInnerProps) => {
  const { $level, $intent, ...rest } = props;
  return <Button {...rest} />;
})<NextButtonInnerProps>`
  ${nextButtonMinimalStyles}
`;

export const IconFix = styled.span`
  :not(:last-child) {
    margin-inline-end: ${theme.token.marginXS}px;
  }
`;
